import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, markRaw, ref } from "vue";
import { useRouter } from "vue-router";
import { useLocalStorage } from "@vueuse/core";
import { DateTime } from "luxon";

import {
    LastPrices,
    ModelPortfolios,
    Portfolio,
    PortfolioMetadata,
    PositionData,
    PriceItem
} from "@/types/api/market-data";

import { useAuthStore } from "@/stores/Auth";
import { usePermissionsStore } from "@/stores/Permissions";

import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload } from "@/types/api/fetch";
import { closedPositionKey, openedPositionKey } from "@/types/products/product-definitions";
import { mtpcProductKey, scccProductKey, tivProductKey, trwProductKey } from "@/types/permissions";
import type { ModelPortfolio, PortfolioColumnDefinition, ModelPortfolioTradeAlert } from "@/types/portfolio";
import { createPortfolio } from "@/types/api/market-data";

import routeManager from "@/router/route-manager";

import { modelPortfolioAvailableColumns } from "@/config/modelPortfolioAvailableColumns";

// Non-reactive variables
import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

// Constant value references
const availablePortfolioHeaderItems: Readonly< Map< string, string > > = modelPortfolioAvailableColumns;

// Stores the list of all possible model portfolios available to the current user
const defaultModelPortfolios: ModelPortfolio[] = [];
const allModelPortfolios = useLocalStorage< ModelPortfolio[] >( 'model-portfolios', defaultModelPortfolios );

// This is a single string that is set to the currently selected model-portfolio key (e.g.; 'checkpoint-trader', 'monthly-trend-trader', etc. )
const selectedPortfolioKey = ref< string >( '' );

// This is a singular boolean that tracks whether the open or closed position is currently selected
const openPositionsSelected = ref< boolean >( true );

// This stores a reference to the headers for the currently selected model portfolio
const selectedHeaders = ref< PortfolioColumnDefinition[] >( [] );

// This stores a reference to the positions for the currently selected model portfolio
const selectedPositions = ref< ModelPortfolioTradeAlert[] >( [] );

// NOTE: The following two state variables should not change all that often and so could be cached on a longer term basis
const openPositionHeaderItems = ref< string[] >( [] );
const closedPositionHeaderItems = ref< string[] >( [] );

const lastUpdate = ref< DateTime | null >( null );
const updateRefreshCoolOffDuration = 3;
const initialized = computed< boolean >( () => {
    return allModelPortfolios.value?.length > 0 &&
        null !== lastUpdate.value &&
        DateTime.now() < lastUpdate.value.plus( { minutes: updateRefreshCoolOffDuration } );
} );

const isLoading = computed< boolean >( () => loading.value );
const loading = ref< boolean >( false );
const error = ref< string >( '' );

export const useModelPortfoliosStore = defineStore( 'model-portfolio', () => {
    const { authToken, isAuthenticated } = storeToRefs( useAuthStore() );
    const permissionsStore = usePermissionsStore();

    /**
     * Clear all state values for this store
     */
    function reset(): void {
        // Clear out the local storage value
        allModelPortfolios.value = null;
        closedPositionHeaderItems.value = [];
        error.value = '';
        loading.value = false;
        openPositionHeaderItems.value = [];
        openPositionsSelected.value = true;
        selectedHeaders.value = [];
        selectedPortfolioKey.value = '';
        selectedPositions.value = [];

        // Reset the local storage variable to the "uninitialized" value
        allModelPortfolios.value = defaultModelPortfolios;
    }

    async function initializeAsync(): Promise< void > {
        // If not authenticated, return immediately
        if ( !isAuthenticated.value ) {
            logger.debug( 'Exiting - not authenticated...' );
            return;
        }

        if ( loading.value ) {
            logger.debug( 'Exiting - already processing...' );
            return;
        }

        if ( initialized.value ) {
            logger.debug( 'Exiting - refresh window is not available yet...' );
            return;
        }

        lastUpdate.value = DateTime.now();

        loading.value = true;
        error.value = '';

        try {
            const productSlugs = permissionsStore.accessibleModelPortfoliosSlugs.join( '|' );

            const result =
                await goFetch< ModelPortfolios, PersistedQueryPayload >( {
                    endpoint: 'marketdata/graphql',
                    payload: {
                        id: '45ed10e12b1cc9db1a51e352d25a98a9',
                        variables: {
                            permissionNameHash: productSlugs
                        }
                    },
                    authToken: authToken.value
                } );

            if ( result?.data ) {
                // cache only the products that are marked as "display-able"
                const response: ModelPortfolios = result.data as ModelPortfolios;
                if ( null != response?.modelPortfoliosByUserAccess?.portfolios ) {
                    const portfoliosReceived = response?.modelPortfoliosByUserAccess?.portfolios;
                    let untrackedOpenRecommendations: ModelPortfolioTradeAlert[] = [];
                    const portfolios: ModelPortfolio[] = portfoliosReceived.map( ( mp: Portfolio ) => {
                        const portfolioKey: string = mp.key.replace( tivProductKey, mtpcProductKey ).replace( trwProductKey, scccProductKey );
                        const configuration: PortfolioMetadata = mp.metadata;

                        // NOTE: HACK ALERT - this is only until we can drive the MP by a pure data driven engine
                        configuration.open_pos_keys =
                            [ ...configuration.open_pos_keys.filter( ( key: string ) => key !== 'first_check_point' && key !== 'next_check_point' ) ];

                        const portfolioData: PositionData = mp.data;
                        const portfolio: ModelPortfolio = createPortfolio( portfolioKey, configuration, portfolioData );

                        // TODO: the following can be removed when we are able to fully track our own data sources
                        portfolio.data
                            .positions
                            .opened
                            .filter( ( recommendation: ModelPortfolioTradeAlert ) => !recommendation.tracked )
                            .forEach( ( rec: ModelPortfolioTradeAlert ) => untrackedOpenRecommendations.push( rec ) );

                        return portfolio;
                    } );

                    // TODO: Remove this when we move to our own analysis engine
                    if ( untrackedOpenRecommendations.length > 0 ) {
                        try {
                            const priceResult =
                                await goFetch< LastPrices, PersistedQueryPayload >( {
                                    endpoint: 'marketdata/graphql',
                                    payload: {
                                        id: '494b33dd9fdf0568aa84024ca95edca7',
                                        variables: {
                                            tickers: untrackedOpenRecommendations.map( ( rec: ModelPortfolioTradeAlert ) => rec.symbol )
                                        }
                                    },
                                    authToken: authToken.value
                                } );

                            if ( priceResult?.data?.securityClosingPriceByTickers ) {
                                priceResult.data.securityClosingPriceByTickers.payload.items.forEach( ( item: PriceItem ) => {
                                    portfolios.forEach( ( portfolio: ModelPortfolio ) => {
                                        const recommendations: ModelPortfolioTradeAlert[] =
                                            portfolio.data.positions.opened.filter(
                                                ( rec: ModelPortfolioTradeAlert ) => item.ticker === rec.symbol );
                                        if ( recommendations ) {
                                            recommendations.forEach( ( rec: ModelPortfolioTradeAlert ) => rec.last_close = item.price.c );
                                        }
                                    } );
                                } );
                            }
                        } catch ( err: any ) {
                            // TODO: Send error to ux-svc
                            logger.error( JSON.stringify( err.message ) );
                            logger.error( JSON.stringify( err.stack ) );
                        }
                    }

                    allModelPortfolios.value = [ ...portfolios ];
                }
            }
        } catch( err: any ) {
            // TODO: Send error to ux-svc
            logger.error( JSON.stringify( err.message ) );
            reset();
        } finally {
            // Clear the "loading" flag
            loading.value = false;
        }
    }

    const activePortfolio =
        computed< ModelPortfolio | undefined >( () =>
                allModelPortfolios.value
                        .find( ( portfolio: ModelPortfolio ): boolean => portfolio.key === selectedPortfolioKey.value ) );
    const productSlug = computed< string >( () => selectedPortfolioKey.value );

    const activeView = computed< string >( () => openPositionsSelected ? openedPositionKey : closedPositionKey );
    const activeHeaders = computed< PortfolioColumnDefinition[] >( () => selectedHeaders.value );
    const activePositions = computed< ModelPortfolioTradeAlert[] >( () => selectedPositions.value );
    const portfolioTitle = computed< string >( () => activePortfolio.value?.configuration.name ?? 'Unknown' );

    const hasError = computed< boolean >( () => error.value?.length > 0 );

    /**
     * Used to assign the selected product's model portfolios
     *
     * @param {string} productSlug - the slug for the product wishing to be set to active
     * reset to display the open positions table upon becoming active
     */
    function setCurrentModelPortfolio( productSlug: string ): void {
        selectedPortfolioKey.value = productSlug;
        adjustSelectedPositions( openedPositionKey );
    }

    /**
     * Used to switch between Open/Closed positions
     */
    function adjustSelectedPositions( key: string ) {
        if ( key === openedPositionKey ) {
            openPositionsSelected.value = true;
            selectedHeaders.value = retrieveOpenHeaders();
            selectedPositions.value = activePortfolio.value?.data.positions.opened ?? [];
        } else {
            openPositionsSelected.value = false;
            selectedHeaders.value = retrieveClosedHeaders();
            selectedPositions.value = activePortfolio.value?.data.positions.closed ?? [];
        }
    }

    const router = markRaw( useRouter() );

    // Used when switching between different products - e.g.; checkpoint-trader -> deep-market-trader
    function switchPortfolioContext( portfolioKey: string, displayOpenPositions: boolean = true ): void {
        const productHasModelPortfolio: ModelPortfolio | undefined =
            allModelPortfolios.value.find( mp => mp.key === portfolioKey?.toLowerCase() );
        if ( productHasModelPortfolio ) {
            if ( !permissionsStore.hasModelPortfolioPermissionsForProduct( portfolioKey ) ) {
                router.push( routeManager.forbiddenPath() ).then();
            } else {
                openPositionsSelected.value = displayOpenPositions;
                setCurrentModelPortfolio( portfolioKey );
            }
        }
    }

    // Used to load the "Open Positions" headers for the currently selected model portfolio
    function retrieveOpenHeaders(): PortfolioColumnDefinition[] {
        const fieldData: Map< string, string > = availablePortfolioHeaderItems;
        const availableKeys: string[] = activePortfolio.value?.configuration?.openPositionKeyList ?? [];
        return availableKeys.reduce( ( current: any, key: string ) => [ ...current, { text: fieldData.get( key ), value: key } ], [] );
    }

    // Used to load the "Closed Positions" headers for the currently selected model portfolio
    function retrieveClosedHeaders(): PortfolioColumnDefinition[] {
        const fieldData: Map< string, string > = availablePortfolioHeaderItems;
        const availableKeys: string[] = activePortfolio.value?.configuration?.closedPositionKeyList ?? [];
        return availableKeys.reduce( ( current: any, key: string ) => [ ...current, { text: fieldData.get( key ), value: key } ], [] );
    }

    return {
        // ACTIONS
        adjustSelectedPositions,
        initializeAsync,
        reset,
        switchPortfolioContext,

        // GETTERS
        activeHeaders,
        activePortfolio,
        activePositions,
        activeView,
        closedPositionHeaderItems,
        error,
        hasError,
        initialized,
        isLoading,
        openPositionsSelected,
        openPositionHeaderItems,
        portfolioTitle,
        productSlug
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useModelPortfoliosStore, import.meta.hot ) );
}
